import Modal from '@/components/layout/Modal';
import { useMutation } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { useState } from 'react';
import toast from 'react-hot-toast';

export type CreationFormModalProps = {
    isOpen: boolean;
    onClose: () => void;
};

export default function CreationFormModal({ isOpen, onClose }: CreationFormModalProps) {
    const [formName, setFormName] = useState('');
    const router = useRouter();

    const newFormBlockList = [
        {
            type: 'text',
            category: 'Text',
            htmlType: 'h1',
            label: 'Here is an example of a title. Click to edit it',
            description: 'A title 1 block',
            icon: 'Heading1',
            textStyle: { color: 'black', fontSize: 32, fontWeight: 'bold', fontFamily: 'Arial, Helvetica, sans-serif' },
            elementStyle: { padding: '21.44px 0px', margin: '0' },
            id: `text-${Date.now()}`,
        },
        {
            type: 'text',
            category: 'Text',
            htmlType: 'p',
            label: 'Here is an example of a description you could add. Click to edit it.',
            description: 'A paragraph block',
            icon: 'Text',
            textStyle: { color: 'black', fontSize: 16, fontWeight: 'normal', fontFamily: 'Arial, Helvetica, sans-serif' },
            elementStyle: { padding: '16px 0px', margin: '0' },
            id: `text-${Date.now() + 1}`,
        },
    ];

    const createFormMutation = useMutation({
        mutationFn: async (formName: string) => {
            const response = await fetch('/api/forms', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    name: formName || `Untitled Form ${new Date().toLocaleString()}`,
                    blocks: newFormBlockList,
                }),
            });

            if (!response.ok) {
                toast.error('Failed to create form');
                throw new Error('Failed to create form');
            }

            const form = await response.json();
            return form.id;
        },
        onSuccess: (newFormId) => {
            if (newFormId) {
                toast.success('Form created successfully, redirecting to form edition...');
                router.push(`/form/${newFormId}/edit`);
                onClose();
            }
        },
        onError: (error) => {
            toast.error('Failed to create form');
            console.error('Error creating new form:', error);
        },
    });

    const handleSubmit = () => {
        createFormMutation.mutate(formName);
    };

    const handleCancel = () => {
        setFormName('');
        onClose();
    };

    return (
        <Modal title="Create a new form" isOpen={isOpen} onCancel={handleCancel} onConfirm={handleSubmit} btnLabel="Create">
            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <p className="mb-2">Please enter a name for your new form:</p>
                    <input
                        id="formName"
                        type="text"
                        name="formName"
                        value={formName}
                        onChange={(e) => setFormName(e.target.value)}
                        placeholder="Untitled Form"
                        className="input input-bordered rounded-md w-full focus:outline-none"
                        autoFocus
                    />
                </div>
            </form>
        </Modal>
    );
}
